<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="750px"
    contentCSS="custom-content-css"
    title=""
  >
    <div
      v-if="contactData !== null"
      class="flex flex-col gap-6 text-[13px] proxima_nova font-normal"
    >
      <div class="flex justify-between align-center">
        <div class="flex justify-items-start gap-2 align-center">
          <ProfileIcon
            class="cursor-pointer"
            v-on:click="goToEntity"
            :data="contactData.contact"
            :size="60"
            :font="22"
          />

          <div>
            <div class="font-semibold text-2xl">
              {{ contactData?.contact?.name }}
            </div>
            <div class="mt-1 text-sm">
              {{ contactData?.contact?.company_name }}
            </div>
          </div>
        </div>
        <div class="flex gap-2">
          <a-button class="primary_medium font-semibold" @click="goToEntity">
            Go to {{ contactData.contact?.entity_type?.toLowerCase() }}
            <ThemeImage
              :icon="imageNames.GO_TO_LINK_GREY"
              alt="go_to_link"
              class="size-4 ml-1"
            />
          </a-button>
          <a-button
            :class="[
              'truncate',
              'ongoing-main-status',
              'cursor-pointer',
              outreachStatusProvider[contactData.execution_status]?.[1] ||
                'default',
            ]"
            disabled
          >
            {{
              outreachStatusProvider[contactData.execution_status]?.[0] ||
              contactData.execution_status
            }}
          </a-button>
        </div>
      </div>

      <div class="mt-4">
        <div v-for="(message, index) in contactData.messages" :key="message.id">
          <div class="flex items-center justify-between">
            <div class="flex justify-items-start align-items-center gap-2">
              <div
                class="flex items-center justify-center w-9 h-9 rounded-full bg_white_8 mr-2"
              >
                <ThemeImage
                  :icon="
                    getStepIcon(message.step_type, message.automation_type)
                  "
                  alt="step_icon"
                  class="size-5"
                />
              </div>

              <div :class="index === 0 ? '-mt-1' : 'mt-1'">
                <div class="flex items-center gap-2">
                  <span class="font-semibold">{{
                    getMessageTypeWithAutomation(
                      message.step_type,
                      message.automation_type,
                    )
                  }}</span>
                  <span v-if="message.variant" class="text-sm secondary_color">
                    ({{ message.variant }})
                  </span>
                </div>
                <div class="text-xs secondary_color mt-1">
                  Day {{ message.relative_day }} ·
                  {{ getStepTimeInfo(message) }}
                </div>
              </div>
            </div>
            <a-tooltip
              v-if="
                message.status === 'Skipped' || message.status === 'Not Sent'
              "
              :title="getStepStatusTooltip(message.status, message)"
              placement="left"
            >
              <span
                :class="[
                  'truncate',
                  'ongoing_status',
                  'cursor-pointer',
                  outreachStatusProvider[message.status]?.[1] || 'default',
                ]"
              >
                {{
                  outreachStatusProvider[message.status]?.[0] || message.status
                }}
              </span>
            </a-tooltip>
            <span
              v-else
              :class="[
                'truncate',
                'ongoing_status',
                'cursor-pointer',
                outreachStatusProvider[message.status]?.[1] || 'default',
              ]"
            >
              {{
                outreachStatusProvider[message.status]?.[0] || message.status
              }}
            </span>
          </div>
          <div
            :class="[
              'pl-8',
              {
                left_border: index !== contactData.messages.length - 1,
              },
              'ml-4',
              'pb-6',
            ]"
          >
            <div
              v-if="
                shouldShowMessageContent(message.step_type, message.status) &&
                message.status === 'Complete'
              "
              class="pt-5"
            >
              <div
                v-if="message.message"
                class="email_textarea_large email_body_wrapper min-h-[175px]"
                v-html="decodeHtmlEntities(getMessageWithSubject(message))"
              ></div>
              <div v-else class="text-gray-400 italic">
                No message content available
              </div>
              
              <div 
                v-if="message.attachments && message.attachments.length > 0" 
                class="mt-4"
              >
                <div class="text-xs font-medium text_color mb-2">
                  Attachments ({{ message.attachments.length }})
                </div>
                <div class="flex flex-wrap gap-2">
                  <AttachedItem
                    v-for="(file, index) in message.attachments"
                    :file="file"
                    :key="`${index}-${file.id}`"
                    :downloadable="true"
                    @remove="() => {}"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else-if="isLoading"
      class="flex flex-col items-center justify-center gap-2 mt-20"
    >
      <CustomLoader :size="28" />
    </div>
    <div v-else class="flex flex-col items-center justify-center gap-2 mt-20">
      <div class="text-sm secondary_color">No contact data available</div>
    </div>
  </CustomDrawer>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import AttachedItem from "@/src/components/attachment_components/AttachedItem.vue";
import SequenceService from "@/src/api/SequenceService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { outreachStatusProvider } from "@/src/constants/statusConstants.js";
import { sequenceStepTypes } from "@/src/constants/sequenceConstants.js";
import { stepOptions } from "@/src/constants/sequenceStepOptions.js";

const props = defineProps({
  sequenceId: {
    type: [String, Number],
    required: true,
  },
  entityId: {
    type: [String, Number],
    required: true,
  },
  entityType: {
    type: String,
    required: true,
  },
  executionId: {
    type: [String, Number],
    required: true,
  },
  drawerVisible: {
    type: Boolean,
    required: true,
  },
});

const router = useRouter();
const internalDrawerVisible = ref(props.drawerVisible);
const contactData = ref(null);
const isLoading = ref(false);
const emit = defineEmits(["update:modelValue"]);

watch(
  () => props.drawerVisible,
  (newVal) => {
    internalDrawerVisible.value = newVal;
  },
);

watch(
  () => internalDrawerVisible.value,
  (newVal) => {
    fetchContactDetails();
    if (!newVal) {
      emit("update:modelValue", false);
    }
  },
);

watch(
  () => props.entityId,
  (newValue) => {
    fetchContactDetails();
  },
);

const fetchContactDetails = async () => {
  isLoading.value = true;

  try {
    const data = await SequenceService.fetchSequenceContactDetails(
      props.sequenceId,
      props.entityId,
      props.entityType,
      props.executionId,
    );

    if (data.success) {
      contactData.value = data;
    } else if (data.no_data) {
      // Handle cases where no data is available
      contactData.value = {
        contact: null,
        messages: [],
        execution_status: "PENDING",
        execution_status_reason:
          data.execution_status_reason || "No recent activity",
      };
    } else {
      message.error(data.message || "Failed to fetch entity details");
      contactData.value = null;
    }
  } catch (error) {
    console.error("Error fetching contact details:", error);

    // Check if it's a 304 response or network error
    if (error.status === 304 || error.message?.includes("304")) {
      contactData.value = {
        contact: null,
        messages: [],
        execution_status: "PENDING",
        execution_status_reason: "No changes detected",
      };
    } else {
      message.error("An error occurred while fetching entity details");
      contactData.value = null;
    }
  } finally {
    isLoading.value = false;
  }
};

const goToEntity = () => {
  if (props.entityType === "Lead") {
    router.push({
      name: "lead-detail",
      params: { id: props.entityId },
    });
  } else if (props.entityType === "Contact") {
    router.push({
      name: "contact-detail",
      params: { id: props.entityId },
    });
  }
};

onMounted(() => {
  fetchContactDetails();
});

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return (
    date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " " +
    date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  );
};

const getMessageTypeWithAutomation = (stepType, automationType) => {
  switch (automationType) {
    case "AI":
      switch (stepType) {
        case sequenceStepTypes.EMAIL:
          return "AI Email";
        case sequenceStepTypes.LINKEDIN_MESSAGE:
          return "AI LinkedIn Message";
        case sequenceStepTypes.LINKEDIN_INMAIL:
          return "AI LinkedIn InMail";
        case sequenceStepTypes.LINKEDIN_INVITE:
          return "AI LinkedIn Invite";
        case sequenceStepTypes.PHONE_CALL:
          return "AI Phone Call";
        case sequenceStepTypes.TASK:
          return "AI Task";
        case sequenceStepTypes.VOICE:
          return "AI Voice Agent";
        case sequenceStepTypes.QUALIFICATION:
          return "AI Qualification Agent";
        case sequenceStepTypes.MARKET_RESEARCH:
          return "AI Market Research Agent";
        case sequenceStepTypes.MULTI_THREADING:
          return "AI Multi Threading Agent";
        case sequenceStepTypes.NEXT_BEST_ACTION:
          return "AI Next Best Action";
        default:
          return `AI ${stepType}`;
      }
    case "AUTOMATED":
      switch (stepType) {
        case sequenceStepTypes.EMAIL:
          return "Automatic Email";
        case sequenceStepTypes.LINKEDIN_MESSAGE:
          return "Automatic LinkedIn Message";
        case sequenceStepTypes.LINKEDIN_INMAIL:
          return "Automatic LinkedIn InMail";
        case sequenceStepTypes.LINKEDIN_INVITE:
          return "Automatic LinkedIn Invite";
        case sequenceStepTypes.PHONE_CALL:
          return "Automatic Phone Call";
        case sequenceStepTypes.TASK:
          return "Automatic Task";
        case sequenceStepTypes.VOICE:
          return "Automatic Voice Agent";
        case sequenceStepTypes.QUALIFICATION:
          return "Automatic Qualification Agent";
        case sequenceStepTypes.MARKET_RESEARCH:
          return "Automatic Market Research Agent";
        case sequenceStepTypes.MULTI_THREADING:
          return "Automatic Multi Threading Agent";
        case sequenceStepTypes.NEXT_BEST_ACTION:
          return "Automatic Next Best Action";
        case sequenceStepTypes.WHATSAPP_MESSAGE:
          return "Automatic Whatsapp Message";
        default:
          return `Automatic ${stepType}`;
      }
    case "TASK":
      switch (stepType) {
        case sequenceStepTypes.TASK:
          return "Manual Task";
        case sequenceStepTypes.PHONE_CALL:
          return "Manual Phone Call";
        case sequenceStepTypes.EMAIL:
          return "Manual Email";
        case sequenceStepTypes.LINKEDIN_MESSAGE:
          return "Manual LinkedIn Message";
        case sequenceStepTypes.LINKEDIN_INMAIL:
          return "Manual LinkedIn InMail";
        case sequenceStepTypes.LINKEDIN_INVITE:
          return "Manual LinkedIn Invite";
        case sequenceStepTypes.PHONE_CALL:
          return "Manual Phone Call";
        case sequenceStepTypes.TASK:
          return "Manual Task";
        case sequenceStepTypes.VOICE:
          return "Manual Voice Agent";
        case sequenceStepTypes.QUALIFICATION:
          return "Manual Qualification Agent";
        case sequenceStepTypes.MARKET_RESEARCH:
          return "Manual Market Research Agent";
        case sequenceStepTypes.MULTI_THREADING:
          return "Manual Multi Threading Agent";
        case sequenceStepTypes.NEXT_BEST_ACTION:
          return "Manual Next Best Action";
        default:
          return `Manual ${stepType}`;
      }
    default:
      switch (stepType) {
        case sequenceStepTypes.EMAIL:
          return "Manual Email";
        case sequenceStepTypes.LINKEDIN_MESSAGE:
          return "Manual LinkedIn Message";
        case sequenceStepTypes.LINKEDIN_INMAIL:
          return "Manual LinkedIn InMail";
        case sequenceStepTypes.LINKEDIN_INVITE:
          return "Manual LinkedIn Invite";
        case sequenceStepTypes.PHONE_CALL:
          return "Manual Phone Call";
        case sequenceStepTypes.TASK:
          return "Manual Task";
        case sequenceStepTypes.VOICE:
          return "Manual Voice Agent";
        case sequenceStepTypes.QUALIFICATION:
          return "Manual Qualification Agent";
        case sequenceStepTypes.MARKET_RESEARCH:
          return "Manual Market Research Agent";
        case sequenceStepTypes.MULTI_THREADING:
          return "Manual Multi Threading Agent";
        case sequenceStepTypes.NEXT_BEST_ACTION:
          return "Manual Next Best Action";
        default:
          return `Manual ${stepType}`;
      }
  }
};

const getStepStatusTooltip = (status, message) => {
  return message?.reason || `Status: ${status}`;
};

const shouldShowMessageContent = (stepType, status) => {
  return (
    [
      sequenceStepTypes.EMAIL,
      sequenceStepTypes.LINKEDIN_MESSAGE,
      sequenceStepTypes.LINKEDIN_INMAIL,
      sequenceStepTypes.LINKEDIN_INVITE,
    ].includes(stepType) && status === "Complete"
  );
};

const getStepIcon = (stepType, automationType) => {
  const category =
    automationType === "AI"
      ? stepOptions?.ai
      : automationType === "AUTOMATED"
        ? stepOptions?.automated
        : stepOptions?.manual;

  const option = category?.options?.find(
    (opt) =>
      opt?.stepType === stepType && opt?.automationType === automationType,
  );

  return option?.icon || imageNames.EMAIL_GREY;
};

const getStepTimeInfo = (message) => {
  const status = String(message.status).toUpperCase();

  if (!message.scheduled_at) {
    return "Pending";
  }

  if (status === "SKIPPED" || status === "NOT SENT") {
    return ``;
  }

  if (status === "IN_PROGRESS" || status === "PENDING") {
    return `${message.scheduled_at}`;
  }

  return `Completed at ${message.scheduled_at}`;
};

const getMessageWithSubject = (message) => {
  let content = "";

  if (message.subject) {
    content += `<div style="font-weight: 600; margin-bottom: 16px; color: var(--text-color);"><b>Subject:</b> ${message.subject}</div>`;
  }

  if (message.message) {
    const htmlMessage = message.message.replace(/\n/g, "<br>");
    content += htmlMessage;
  }

  return content;
};

const decodeHtmlEntities = (html) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  return textarea.value;
};
</script>

<style scoped>
.ongoing_status {
  padding: 4px 8px;
  border-radius: 24px;
  font-weight: 600;
  cursor: pointer;
  color: white;
  width: 100px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ongoing-main-status {
  color: white;
  padding: 4px 12px;
  border-radius: 24px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ongoing-main-status:hover {
  color: white;
  border: none;
}

.default,
.in_active {
  background: var(--default-icon);
}

.cancelled {
  background: #fa3523;
}

.paused {
  background: var(--paused-icon);
}

.in_progress {
  background: #a05bd5;
}

.unqualified,
.pending {
  background: var(--ended-icon);
}

.ended {
  background: var(--ended-icon);
}

.complete,
.active {
  background: #2d6711;
}

.researching {
  background: var(--researching-icon);
}

.generating {
  background: #877ae1;
}

.to_review {
  background: #3773e4;
}

.replied {
  background: #118456;
}

.removed {
  background: #ac1c1e;
}

.bounced {
  background: var(--bounced-icon);
}

.unsubscribed {
  background: #ac1c1e;
}

.left_border {
  border-color: var(--white-opacity-12);
  border-left-width: 1px;
}

.email_body_wrapper :deep(ol) {
  list-style-type: decimal;
  padding-left: 2.5rem;
}

.email_body_wrapper :deep(ul) {
  list-style-type: disc;
  padding-left: 2.5rem;
}

.email_body_wrapper :deep(ol li[data-list="bullet"]) {
  list-style-type: disc !important;
}

.email_body_wrapper :deep(ol li[data-list="bullet"])::marker {
  content: "• " !important;
}
</style>
